/**
 * FX rates — `/ledger/fx-rates`.
 *
 * A rate is always quote-per-base: one unit of the base currency costs `rate`
 * units of the quote currency. The form says so in words next to the field,
 * because a reversed pair is the single most expensive typo on this screen and
 * the contract has no inverse form to fall back on.
 *
 * Ingested rates arrive from a provider feed and are read-only here. A manual
 * rate is the human exception, so it demands a controlled reason code that is
 * stored with it — the screen cannot offer "other" as a free-text excuse.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { formatCalendarDate } from "@/lib/format";
import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  FX_RATE_TYPES,
  MANUAL_FX_REASONS,
  createManualFxRate,
  listFxRates,
  type FxRate,
  type FxRateFilters,
  type FxRateType,
  type ManualFxReason,
} from "../calendar-api";
import { ledgerKeys } from "../queries";

const ALL = "__all__";
const CCY = /^[A-Z]{3}$/;
/** NUMERIC(20,10), strictly positive: up to ten fractional digits. */
const RATE = /^\d{1,10}(\.\d{1,10})?$/;

export interface FxRatesPageProps {
  canCreateManual?: boolean;
}

export function FxRatesPage({ canCreateManual = false }: FxRatesPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [base, setBase] = useState("");
  const [quote, setQuote] = useState("");
  const [rateType, setRateType] = useState<string>(ALL);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    base_ccy: "SAR",
    quote_ccy: "",
    rate: "",
    valid_on: "",
    reason: "market_source_unavailable" as ManualFxReason,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo<FxRateFilters>(() => {
    const built: FxRateFilters = {};
    if (CCY.test(base)) built.base_ccy = base;
    if (CCY.test(quote)) built.quote_ccy = quote;
    if (rateType !== ALL) built.rate_type = rateType as FxRateType;
    return built;
  }, [base, quote, rateType]);

  const rates = useQuery<FxRate[]>({
    queryKey: ledgerKeys.fxRates.list({ ...filters }),
    queryFn: async ({ signal }) => [...(await listFxRates(filters, undefined, signal)).items],
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  const valid =
    CCY.test(form.base_ccy.toUpperCase()) &&
    CCY.test(form.quote_ccy.toUpperCase()) &&
    form.base_ccy.toUpperCase() !== form.quote_ccy.toUpperCase() &&
    RATE.test(form.rate) &&
    Number(form.rate) > 0 &&
    form.valid_on !== "";

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await createManualFxRate(
        {
          base_ccy: form.base_ccy.toUpperCase(),
          quote_ccy: form.quote_ccy.toUpperCase(),
          rate: form.rate,
          valid_on: form.valid_on,
          manual_reason_code: form.reason,
        },
        newIdempotencyKey(),
      );
      await queryClient.invalidateQueries({ queryKey: ledgerKeys.fxRates.all });
      setOpen(false);
      setForm({ ...form, quote_ccy: "", rate: "", valid_on: "" });
    } catch {
      setError(t("ledger.fxRates.saveError", "The rate could not be recorded."));
    } finally {
      setBusy(false);
    }
  };

  const rows = rates.data ?? [];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">
            {t("ledger.fxRates.title", "Exchange rates")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "ledger.fxRates.subtitle",
              "One unit of the base currency, priced in the quote currency.",
            )}
          </p>
        </div>
        {canCreateManual ? (
          <Button type="button" onClick={() => setOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            {t("ledger.fxRates.new", "Record a manual rate")}
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="fx-base">{t("ledger.fxRates.base", "Base")}</Label>
          <Input
            id="fx-base"
            dir="ltr"
            maxLength={3}
            className="w-24 font-mono uppercase"
            value={base}
            onChange={(event) => setBase(event.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fx-quote">{t("ledger.fxRates.quote", "Quote")}</Label>
          <Input
            id="fx-quote"
            dir="ltr"
            maxLength={3}
            className="w-24 font-mono uppercase"
            value={quote}
            onChange={(event) => setQuote(event.target.value.toUpperCase())}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="fx-type">{t("ledger.fxRates.type", "Kind")}</Label>
          <Select value={rateType} onValueChange={setRateType}>
            <SelectTrigger id="fx-type" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("filters.all", "All")}</SelectItem>
              {FX_RATE_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`ledger.fxRateType.${value}`, value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {rates.isLoading ? (
            <p className="p-4 text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t("ledger.fxRates.empty", "No rates match these filters.")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[40rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fxRates.pair", "Pair")}
                    </th>
                    <th scope="col" className="p-3 text-end">
                      {t("ledger.fxRates.rate", "Rate")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fxRates.validOn", "Valid on")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fxRates.type", "Kind")}
                    </th>
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.fxRates.source", "Source")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="p-3">
                        <span dir="ltr" className="font-mono text-xs">
                          {row.base_ccy}/{row.quote_ccy}
                        </span>
                      </td>
                      <td className="p-3 text-end">
                        <span dir="ltr" className="tabular-nums">
                          {row.rate}
                        </span>
                      </td>
                      <td className="p-3">{day(row.valid_on)}</td>
                      <td className="p-3">
                        <Badge variant={row.rate_type === "manual" ? "secondary" : "outline"}>
                          {t(`ledger.fxRateType.${row.rate_type}`, row.rate_type)}
                        </Badge>
                        {row.manual_reason_code === undefined ? null : (
                          <span className="ms-2 text-xs text-muted-foreground">
                            {t(`ledger.fxReason.${row.manual_reason_code}`, row.manual_reason_code)}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                          {row.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("ledger.fxRates.new", "Record a manual rate")}</DialogTitle>
            <DialogDescription>
              {t(
                "ledger.fxRates.formHint",
                "A manual rate is an exception to the provider feed, so it is stored with the reason it was needed.",
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fx-form-base">{t("ledger.fxRates.base", "Base")}</Label>
              <Input
                id="fx-form-base"
                dir="ltr"
                maxLength={3}
                className="font-mono uppercase"
                value={form.base_ccy}
                onChange={(event) =>
                  setForm({ ...form, base_ccy: event.target.value.toUpperCase() })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fx-form-quote">{t("ledger.fxRates.quote", "Quote")}</Label>
              <Input
                id="fx-form-quote"
                dir="ltr"
                maxLength={3}
                className="font-mono uppercase"
                value={form.quote_ccy}
                onChange={(event) =>
                  setForm({ ...form, quote_ccy: event.target.value.toUpperCase() })
                }
              />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="fx-form-rate">{t("ledger.fxRates.rate", "Rate")}</Label>
              <Input
                id="fx-form-rate"
                dir="ltr"
                inputMode="decimal"
                className="font-mono"
                value={form.rate}
                onChange={(event) => setForm({ ...form, rate: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {t("ledger.fxRates.rateHint", "1 {{base}} = {{rate}} {{quote}}", {
                  base: form.base_ccy || "—",
                  rate: form.rate || "…",
                  quote: form.quote_ccy || "—",
                })}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fx-form-date">{t("ledger.fxRates.validOn", "Valid on")}</Label>
              <Input
                id="fx-form-date"
                type="date"
                value={form.valid_on}
                onChange={(event) => setForm({ ...form, valid_on: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fx-form-reason">{t("ledger.fxRates.reason", "Reason")}</Label>
              <Select
                value={form.reason}
                onValueChange={(value) => setForm({ ...form, reason: value as ManualFxReason })}
              >
                <SelectTrigger id="fx-form-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_FX_REASONS.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`ledger.fxReason.${value}`, value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {error === null ? null : (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t("actions.cancel", "Cancel")}
            </Button>
            <Button type="button" disabled={!valid || busy} onClick={() => void save()}>
              {t("actions.save", "Save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
